import { parseISO, differenceInYears } from 'date-fns';
import {
  courseDiscountTypeByStates,
  courseDiscountsAges,
  additionalDriverDiscountsTypesByStates,
  seniorDiscountByAgeOnlyStates,
  courseDiscountTypes,
  communityDriveAvailableStates
} from '@ourbranch/lookups';

const fullTimeStudentAgeLimit = 23;

// MARK: full time student
export const canPersonBeFullTimeStudent = ({ driverOrPerson, driversOrPeople, rateControlDate }) => {
  const isPrimaryMarried = driversOrPeople.some((d) => {
    return d.isPrimary && d.maritalStatus === 'M';
  });
  const isSpouseOfPrimary = driverOrPerson.isCoApplicant && driverOrPerson.maritalStatus === 'M' && isPrimaryMarried;

  // Use age node if available, otherwise calculate age based on rateControlDate so it doesn't change midterm on a policy
  // since age is re-calculated on renewal
  let age;
  if (driverOrPerson.age !== undefined && driverOrPerson.age !== null) {
    age = driverOrPerson.age;
  } else {
    const birthDate = parseISO(driverOrPerson.dateOfBirth);
    const rateDate = parseISO(rateControlDate);
    age = differenceInYears(rateDate, birthDate);
  }

  // if under 23, and not primary, and not married to primary, then eligible for student discount
  const canBeFullTimeStudent = age < fullTimeStudentAgeLimit && !driverOrPerson.isPrimary && !isSpouseOfPrimary;

  return canBeFullTimeStudent;
};

// Drivers can be persisted with fullTimeStudent=false while schoolName/schoolLocation/goodStudent
// are still set (the student switch historically didn't cascade-clear on uncheck). driver.jsx
// clears those fields via setFieldValue on every mount, so initialValues built straight from the
// stored driver leave Formik permanently dirty — locking the offer footer on "Update offer"
// (https://github.com/gobranch/branch/issues/31834). Pre-apply the same clearing when building
// initialValues so the form starts in the state the mount effects will produce.
export const normalizeDriversStudentFields = ({ drivers, rateControlDate }) => {
  if (!drivers) {
    return drivers;
  }
  return drivers.map((driver) => {
    const canBeFullTimeStudent = canPersonBeFullTimeStudent({
      driverOrPerson: driver,
      driversOrPeople: drivers,
      rateControlDate
    });
    if (canBeFullTimeStudent && driver.fullTimeStudent) {
      return driver;
    }
    const alreadyClean =
      !driver.fullTimeStudent && !driver.goodStudent && driver.schoolName == null && driver.schoolLocation == null;
    if (alreadyClean) {
      return driver;
    }
    return { ...driver, fullTimeStudent: false, goodStudent: false, schoolName: null, schoolLocation: null };
  });
};

// MARK: driving course
export const canPersonReceiveDrivingCourseDiscount = ({ driverOrPerson, state }) => {
  const today = new Date();
  const birthDate = new Date(driverOrPerson.dateOfBirth);
  const age = differenceInYears(today, birthDate);
  const drivingCourseDiscountType = courseDiscountTypeByStates[state];

  // check if there is a state specific age limit for the discount (different from Inexperienced)
  const canReceiveDrivingCourseDiscount =
    drivingCourseDiscountType &&
    drivingCourseDiscountType !== courseDiscountTypes.INEXPERIENCED &&
    (seniorDiscountByAgeOnlyStates[state]
      ? age >= seniorDiscountByAgeOnlyStates[state]
      : age >= courseDiscountsAges[state]);

  // check if there is a state specific age to calculate Inexperienced discount
  const canReceiveInexperiencedDrivingCourseDiscount =
    drivingCourseDiscountType &&
    drivingCourseDiscountType === courseDiscountTypes.INEXPERIENCED &&
    age - driverOrPerson.ageFirstLicensed < courseDiscountsAges[state];

  return {
    canReceiveDrivingCourseDiscount,
    canReceiveInexperiencedDrivingCourseDiscount,
    canReceiveAdditionalDrivingCourseDiscount: additionalDriverDiscountsTypesByStates[state]?.condition
  };
};

// MARK: community drive
export const canReceiveCommunityDriveDiscount = ({ state, isBix }) => {
  return (
    communityDriveAvailableStates[state] &&
    ((isBix && communityDriveAvailableStates[state].showForBix) ||
      (!isBix && communityDriveAvailableStates[state].showForNonBix))
  );
};
